<template>
  <div :id="`${source}_view_details_dropdown`">
    <CustomDropdown
      ref="viewDetailsDropdownRef"
      max-height="500px"
      gap="10px"
      position="start"
      :relative-to-viewport="true"
    >
      <template #trigger>
        <div
          class="view_details_trigger flex flex-row items-center gap-[6px] rounded-lg h-[42px] px-4 cursor-pointer"
        >
          <img
            :src="imagePath.dark.action.viewDetailsIcon"
            alt="view_details_icon"
            class="size-5"
          />
          <span class="text-base">View Details</span>
        </div>
      </template>

      <template #default>
        <div class="flex flex-col p-2">
          <CustomTabs
            v-model="activeKey"
            content-padding="pt-2"
            :tabs="computedTabs"
          >
            <template #global>
              <div class="flex flex-row gap-[6px] items-center">
                <ThemeImage
                  :icon="imageNames.VIEW_DETAILS_EXPAND_GREY"
                  alt="view_details_expand_icon"
                  @click="redirectToCampaignDetails"
                  class="size-4 cursor-pointer"
                />

                <ThemeImage
                  :icon="imageNames.CROSS_GREY"
                  @click="handleCloseDetails"
                  class="size-4 cursor-pointer"
                  alt="cross_grey_icon"
                />
              </div>
            </template>

            <template
              v-for="(tab, index) in computedTabs"
              :key="index"
              #[`tab_content-${index}`]
            >
              <CallCampaignStatusPanel
                :is="tab.component"
                v-bind="getComponentProps(tab.id)"
                :visible="activeKey === index"
                :loading="loadingLeads && pagination.current === 1"
                :loading-more="loadingLeads && pagination.current > 1"
                @load-more="handleLoadMore"
              />
            </template>
          </CustomTabs>
        </div>
      </template>
    </CustomDropdown>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import { toGetCallCampaignLeadsList } from "@/src/utils/callsUtils.js";
import store from "@/src/store/index.js";

import CustomTabs from "@/src/components/CustomTabs.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import CallCampaignStatusPanel from "@/src/views/calls/call_campaigns/campaign_banner/CallCampaignStatusPanel.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import imagePath from "@/src/paths/imagePath.js";

const router = useRouter();
const props = defineProps({
  source: { type: String, required: true },
});

const viewDetailsDropdownRef = ref(null);
const activeKey = ref(0);
const leadsData = ref({
  total: [],
  pending: [],
  answered: [],
  skipped: [],
  voicemails: [],
});
const loadingLeads = ref(false);
const pagination = ref({
  current: 1,
  pageSize: 10,
  total: 0,
});

const campaignId = computed(
  () => store.getters["callsCampaign/currentCampaignId"],
);
const campaignDetails = computed(
  () => store.getters["callsCampaign/campaignDetails"],
);

const statusMap = {
  total: "",
  pending: "pending",
  answered: "answered",
  skipped: "skipped",
  voicemails: "voicemail",
};

const calculateCount = (status) => {
  if (status === "total") return campaignDetails.value?.total_contacts || 0;
  if (status === "pending") return campaignDetails.value?.pending_calls || 0;
  if (status === "answered") return campaignDetails.value?.answered_calls || 0;
  if (status === "skipped") return campaignDetails.value?.skipped_calls || 0;
  if (status === "voicemails")
    return campaignDetails.value?.voicemail_calls || 0;
  return 0;
};

const computedTabs = computed(() => [
  { id: "total", title: "Total", count: calculateCount("total") },
  { id: "pending", title: "Pending", count: calculateCount("pending") },
  { id: "answered", title: "Answered", count: calculateCount("answered") },
  { id: "skipped", title: "Skipped", count: calculateCount("skipped") },
  {
    id: "voicemails",
    title: "Voicemails",
    count: calculateCount("voicemails"),
  },
]);

const getComponentProps = (tabId) => {
  return {
    data: leadsData.value[tabId] || [],
    count: calculateCount(tabId),
    loading: loadingLeads.value && pagination.value.current === 1,
    loadingMore: loadingLeads.value && pagination.value.current > 1,
    hasMore:
      leadsData.value[tabId]?.length < pagination.value.total &&
      leadsData.value[tabId]?.length % pagination.value.pageSize === 0,
  };
};

const handleCloseDetails = () => {
  try {
    viewDetailsDropdownRef.value?.closeDropdown();
  } catch (error) {
    console.error("An error occurred while handling close details: ", error);
  }
};

const redirectToCampaignDetails = () => {
  try {
    router.push({
      name: "sales-dialer-campaign-detail",
      params: { id: campaignId.value },
    });
  } catch (error) {
    console.error(
      "An error occurred while redirecting to campaign details: ",
      error,
    );
  }
};

const gettingCampaignLeads = async (status = "", isLoadMore = false) => {
  if (!campaignId.value) return;

  try {
    loadingLeads.value = true;
    const params = {
      page: pagination.value.current,
      page_size: pagination.value.pageSize,
      status: status,
    };

    const response = await toGetCallCampaignLeadsList(campaignId.value, params);
    if (response) {
      const statusKey =
        status === ""
          ? "total"
          : Object.keys(statusMap).find((key) => statusMap[key] === status);
      if (statusKey) {
        if (isLoadMore) {
          leadsData.value[statusKey] = [
            ...leadsData.value[statusKey],
            ...response.lead_list,
          ];
        } else {
          leadsData.value[statusKey] = response.lead_list;
        }
      }
      pagination.value.total = response.total;
    }
  } catch (error) {
    console.error(
      "An error occurred while getting call campaign leads: ",
      error,
    );
  } finally {
    loadingLeads.value = false;
  }
};

const handleLoadMore = async () => {
  pagination.value.current += 1;
  const selectedTab = computedTabs.value[activeKey.value];
  await gettingCampaignLeads(statusMap[selectedTab.id], true);
};

watch(activeKey, async (newIndex) => {
  pagination.value.current = 1;
  const selectedTab = computedTabs.value[newIndex];
  await gettingCampaignLeads(statusMap[selectedTab.id]);
});

watch(
  () => viewDetailsDropdownRef.value?.isOpen,
  async (newVal) => {
    if (newVal) {
      pagination.value.current = 1;
      const selectedTab = computedTabs.value[activeKey.value];
      await gettingCampaignLeads(statusMap[selectedTab.id]);
    }
  },
);

watch(campaignId, async (newVal) => {
  if (newVal && viewDetailsDropdownRef.value?.isOpen) {
    pagination.value.current = 1;
    const selectedTab = computedTabs.value[activeKey.value];
    await gettingCampaignLeads(statusMap[selectedTab.id]);
  }
});
</script>

<style scoped>
.view_details_trigger {
  transition: background-color 0.4s ease-in-out;
}

.view_details_trigger:hover {
  background-color: var(--white-opacity-8);
}
</style>
